/**
 * Auto-assigns the nearest accruing provider from the customer's approximate
 * location, so a retail order still credits a clinic when the customer never
 * picks one themselves.
 *
 * Priority rules, in order - this module only ever writes the weakest one:
 *   provider-source "url"     a ?provider= referral link, never overwritten
 *   provider-source "manual"  the customer's own pick, never overwritten
 *   provider-source "auto"    written here, and re-writable here
 *
 * Location is resolved by IP rather than navigator.geolocation on purpose: the
 * brief is not to prompt or block the customer before add to cart, and a
 * permission prompt does both.
 */
(function () {
  if (window.__providerAutoAssignLoaded) return;
  window.__providerAutoAssignLoaded = true;

  const PROVIDER_API = "https://admin-aizm.onrender.com/";

  // /providers/near only accepts these two, in MILES - anything else is a 400.
  const RADII_MILES = [25, 50];

  // How far "your local provider" can stretch before it stops being a claim
  // about this customer at all. Measured against the live list, there is a wide
  // gap to aim at: every plausible case lands under 260 miles - London 37,
  // Toronto 53, Auckland 70, Bangor ME 109, Liberal KS 196, Paris 231,
  // Amsterdam 259 - while the implausible ones start at 1,371 (Sydney), 3,409
  // (Tokyo), 3,440 (Hanoi). Mexico City sits alone at 464 and is deliberately
  // excluded: crediting a clinic that far away is not attribution, it is noise
  // in the reporting Part 2 reads back.
  //
  // Note this is not a US-only network. Five accruing providers sit outside it
  // - Reading UK, Vancouver, Nice, Hamilton NZ, Doha - so gating on country
  // would lock out the customers they exist to serve, quite apart from the
  // country field holding "USS" and "ISA" next to "USA".
  const MAX_MILES = 300;

  const LOCATION_CACHE_KEY = "epicutis:auto-assign:location";
  const LOCATION_TTL_MS = 30 * 60 * 1000;
  const ATTEMPT_KEY = "epicutis:auto-assign:attempts";
  // Tries are counted per location, not per session. The expensive half - the
  // radius queries and, past those, the full accruing list - is worth stopping
  // once a place is known to have nothing. But a customer whose network
  // changes, or whose first lookup landed somewhere odd, lands somewhere new,
  // and that deserves a fresh budget rather than a session locked out on the
  // strength of coordinates it no longer has.
  const MAX_ATTEMPTS_PER_LOCATION = 3;
  const REQUEST_TIMEOUT_MS = 8000;
  const WRITER_TIMEOUT_MS = 10000;

  const log = (...args) => console.log("[ProviderAutoAssign]", ...args);
  const warn = (...args) => console.warn("[ProviderAutoAssign]", ...args);

  function session(key, value) {
    try {
      if (value === undefined) {
        const raw = window.sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      }
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return value;
    } catch (error) {
      // Private mode, blocked storage - degrade to "no cache" rather than throw.
      return null;
    }
  }

  async function getJSON(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Response status: ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function toNumber(value) {
    const n = typeof value === "string" ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : null;
  }

  function distanceMiles(lng1, lat1, lng2, lat2) {
    const R = 3958.7613;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  // --- Cart ------------------------------------------------------------------

  async function readCartAttributes() {
    const cart = await getJSON("/cart.js");
    return cart.attributes || {};
  }

  // Merge-writes a single key. /cart/update.js merges by key, so this leaves
  // locksmith, __bogos_tracking and the provider keys alone.
  async function writeCustomerLocation(zip) {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { customer_location: zip } }),
    });
  }

  // --- Location --------------------------------------------------------------

  async function locateByIp() {
    // geojs returns coordinates directly and has no key or quota to trip over;
    // ipwho.is is the backup. ipapi.co was rejected - it rate-limits hard.
    const services = [
      {
        url: "https://get.geojs.io/v1/ip/geo.json",
        read: (d) => ({ lng: toNumber(d.longitude), lat: toNumber(d.latitude), zip: null }),
      },
      {
        url: "https://ipwho.is/",
        read: (d) => ({ lng: toNumber(d.longitude), lat: toNumber(d.latitude), zip: d.postal || null }),
      },
    ];

    for (const service of services) {
      try {
        const point = service.read(await getJSON(service.url));
        if (point.lng !== null && point.lat !== null) return point;
      } catch (error) {
        warn("IP lookup failed, trying next:", service.url, error.message);
      }
    }
    return null;
  }

  async function locateByZip(zip) {
    if (!/^\d{5}$/.test(String(zip || "").trim())) return null;
    try {
      const data = await getJSON(`https://api.zippopotam.us/us/${zip}`);
      const place = data.places && data.places[0];
      if (!place) return null;
      return { lng: toNumber(place.longitude), lat: toNumber(place.latitude), zip: String(zip) };
    } catch (error) {
      warn("ZIP geocode failed:", error.message);
      return null;
    }
  }

  // Only reads a permission that was already granted. Never calls
  // getCurrentPosition speculatively, which would raise the browser prompt.
  async function locateByBrowser() {
    if (!navigator.geolocation || !navigator.permissions) return null;
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      if (status.state !== "granted") return null;
      return await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lng: pos.coords.longitude, lat: pos.coords.latitude, zip: null }),
          () => resolve(null),
          { timeout: REQUEST_TIMEOUT_MS, maximumAge: LOCATION_TTL_MS }
        );
      });
    } catch (error) {
      return null;
    }
  }

  async function resolveLocation(cartAttributes) {
    const cached = session(LOCATION_CACHE_KEY);
    if (cached && Date.now() - cached.timestamp < LOCATION_TTL_MS) {
      log("Using cached location");
      return cached.point;
    }

    return (
      (await locateByIp()) ||
      (await locateByZip(cartAttributes.customer_location)) ||
      (await locateByBrowser())
    );
  }

  // Only a location that actually produced a provider is worth remembering.
  // Caching one that found nothing would pin the customer to it for the next
  // half hour: every reload would reuse the dead coordinates instead of asking
  // again, so a changed network - a VPN coming up, a phone moving from mobile
  // data to wifi - could never recover, and the attempt cap would run out
  // against a location already known to be useless.
  function rememberLocation(point) {
    if (point) session(LOCATION_CACHE_KEY, { point, timestamp: Date.now() });
  }

  // --- Matching --------------------------------------------------------------

  function firstAccruing(providers) {
    if (!Array.isArray(providers)) return null;
    // /providers/near already sorts by distance ascending, so the first
    // accruing entry is the nearest one. The API filters nothing by status:
    // it will happily return Draft and Active - Non-accruing providers, and a
    // non-accruing provider earns the clinic no credit while looking perfectly
    // normal in the UI.
    return providers.find((p) => window.isAccruingProvider && window.isAccruingProvider(p)) || null;
  }

  // Everything accruing, fetched once per page view and reused. ~760KB, so it
  // is only ever reached when the two radius queries came back empty - a
  // customer in a city never gets here. The alternative, /providers/nearest,
  // cannot be used: it returns exactly one record and ignores status, so at
  // Liberal KS it answers with a non-accruing provider and leaves nothing to
  // fall back to. Filtering by ?state= is no good either, the field is stored
  // inconsistently ("KS" alongside "Arizona") and the query is case-sensitive.
  // A status filter on /providers/near would make all of this unnecessary.
  let allAccruing = null;
  async function fetchAllAccruing() {
    if (!allAccruing) {
      allAccruing = await getJSON(`${PROVIDER_API}providers?status=Active`);
    }
    return allAccruing;
  }

  async function findNearestAccruing(lng, lat) {
    for (const radius of RADII_MILES) {
      const providers = await getJSON(
        `${PROVIDER_API}providers/near?lng=${lng}&lat=${lat}&radius=${radius}&skip=0`
      );
      const match = firstAccruing(providers);
      if (match) return { provider: match, miles: null };
    }

    log("Nothing within 50 miles, widening to the full accruing list");
    const everyone = await fetchAllAccruing();
    if (!Array.isArray(everyone)) return null;

    let best = null;
    for (const provider of everyone) {
      const coords = provider.location && provider.location.coordinates;
      // GeoJSON order is [lng, lat], not [lat, lng].
      if (!Array.isArray(coords) || coords.length < 2) continue;
      const miles = distanceMiles(lng, lat, coords[0], coords[1]);
      if (!best || miles < best.miles) best = { provider, miles };
    }
    if (!best) return null;

    if (best.miles > MAX_MILES) {
      log(`Nearest accruing provider is ${Math.round(best.miles)}mi away, leaving unassigned`);
      return null;
    }
    return best;
  }

  // --- Orchestration ---------------------------------------------------------

  function announce(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  // Coarse enough that normal drift within a city stays one place, fine enough
  // that a different city is a different one.
  function locationKey(point) {
    if (!point) return "none";
    return `${point.lng.toFixed(2)},${point.lat.toFixed(2)}`;
  }

  function triesFor(key) {
    const record = session(ATTEMPT_KEY);
    return record && record.at === key ? record.n || 0 : 0;
  }

  async function autoAssign() {
    const attributes = await readCartAttributes();
    const existingCode = attributes["provider-code"];
    const existingSource = attributes["provider-source"];

    if (existingCode && existingSource !== "auto") {
      // "url" and "manual" outrank us. A code with no source predates this
      // feature, so treat it as a deliberate choice too.
      log(`Keeping existing provider (${existingSource || "no source"}):`, existingCode);
      return;
    }

    announce("provider:auto-assign:start");

    // Resolving the location comes first because the budget is keyed to it. It
    // is the cheap half: a few hundred bytes against the 13KB a radius query
    // costs and the ~760KB the widened search does.
    const point = await resolveLocation(attributes);
    const key = locationKey(point);
    const tries = triesFor(key);

    if (tries >= MAX_ATTEMPTS_PER_LOCATION) {
      log("Already tried this location enough times:", key);
      // Still announce, or anything showing a loading state for us waits out
      // its own timeout on every page the customer opens from here on.
      announce("provider:auto-assign:none", { reason: "attempt-limit" });
      return;
    }
    session(ATTEMPT_KEY, { at: key, n: tries + 1 });

    if (!point) {
      log("Could not resolve a location");
      announce("provider:auto-assign:none", { reason: "no-location" });
      return;
    }

    const match = await findNearestAccruing(point.lng, point.lat);
    if (!match) {
      log("No accruing provider within range");
      announce("provider:auto-assign:none", { reason: "no-provider" });
      return;
    }

    rememberLocation(point);

    if (match.provider.providerCode === existingCode) {
      log("Nearest provider is already assigned:", existingCode);
      announce("provider:auto-assign:done", { provider: match.provider, unchanged: true });
      return;
    }

    await window.addToCartAttribute(match.provider.providerCode, match.provider, "auto");
    log(`Assigned ${match.provider.name}`, match.miles === null ? "" : `(${Math.round(match.miles)}mi)`);

    const zip = point.zip || match.provider.zipCode;
    if (zip && !attributes.customer_location) await writeCustomerLocation(zip);

    announce("provider:auto-assign:done", { provider: match.provider });
  }

  // This file is loaded from the head, but window.addToCartAttribute comes from
  // provider-locator-map.js, which sections/provider-modal.liquid loads much
  // further down the document. Deferred scripts run while readyState is already
  // "interactive", so neither a readyState check nor DOMContentLoaded is a
  // reliable signal that the writer exists yet - wait for the function itself.
  async function waitForWriter() {
    const deadline = Date.now() + WRITER_TIMEOUT_MS;
    while (typeof window.addToCartAttribute !== "function") {
      if (Date.now() > deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return true;
  }

  async function start() {
    if (!(await waitForWriter())) {
      warn("addToCartAttribute never became available, skipping auto-assign");
      announce("provider:auto-assign:none", { reason: "no-writer" });
      return;
    }
    await autoAssign();
  }

  // Never let a failure here break the page it runs on.
  start().catch((error) => {
    warn("Auto-assign failed:", error.message);
    announce("provider:auto-assign:none", { reason: "error" });
  });
})();
